import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { allProperties, testimonials } from '../../mocks/properties';
import { blogPosts } from '../../mocks/blog';
import PropertyFilter, { FilterState } from './components/PropertyFilter';
import PropertiesMap from './components/PropertiesMap';
import LanguageSelector, { LangCode, getTranslation } from '../../components/feature/LanguageSelector';

export default function HomePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState<LangCode>('es');
  const t = (key: string) => getTranslation(lang, key);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [newsletter, setNewsletter] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [filteredProperties, setFilteredProperties] = useState(allProperties);
  const [activeFilters, setActiveFilters] = useState<FilterState | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFilter = (filters: FilterState) => {
    setActiveFilters(filters);
    let results = [...allProperties];

    if (filters.operationType !== 'all') {
      if (filters.operationType === 'construccion') {
        results = results.filter(p => p.tags && p.tags.includes('construccion'));
      } else if (filters.operationType === 'parcelas') {
        results = results.filter(p =>
          p.tags && p.tags.includes('parcelas') && !p.type.toLowerCase().includes('alquiler')
        );
      } else {
        const keyword = filters.operationType === 'venta' ? 'Venta' : 'Alquiler';
        results = results.filter(p => p.type.toLowerCase().includes(keyword.toLowerCase()));
      }
    }
    if (filters.location.trim()) {
      results = results.filter(p =>
        p.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    if (filters.priceMin) {
      const min = parseInt(filters.priceMin, 10);
      results = results.filter(p => {
        const price = parseInt(p.price.replace(/[^0-9]/g, ''), 10);
        return price >= min;
      });
    }
    if (filters.priceMax) {
      const max = parseInt(filters.priceMax, 10);
      results = results.filter(p => {
        const price = parseInt(p.price.replace(/[^0-9]/g, ''), 10);
        return price <= max;
      });
    }
    if (filters.bedrooms) {
      if (filters.bedrooms === '4+') {
        results = results.filter(p => p.bedrooms >= 4);
      } else {
        results = results.filter(p => p.bedrooms === parseInt(filters.bedrooms, 10));
      }
    }
    if (filters.bathrooms) {
      if (filters.bathrooms === '4+') {
        results = results.filter(p => p.bathrooms >= 4);
      } else {
        results = results.filter(p => p.bathrooms === parseInt(filters.bathrooms, 10));
      }
    }
    if (filters.areaMin) {
      const min = parseInt(filters.areaMin, 10);
      results = results.filter(p => {
        const area = parseInt(p.area.replace(/[^0-9]/g, ''), 10);
        return area >= min;
      });
    }
    if (filters.areaMax) {
      const max = parseInt(filters.areaMax, 10);
      results = results.filter(p => {
        const area = parseInt(p.area.replace(/[^0-9]/g, ''), 10);
        return area <= max;
      });
    }

    setFilteredProperties(results);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      setFormStatus('error');
      return;
    }
    setFormStatus('sending');
    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      }).toString();
      const response = await fetch('https://readdy.ai/api/form/d6ng78aqoe30lj0v8npg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletter) {
      setNewsletterStatus('error');
      return;
    }
    setNewsletterStatus('sending');
    try {
      const formBody = new URLSearchParams({ email: newsletter }).toString();
      const response = await fetch('https://readdy.ai/api/form/d6ng78aqoe30lj0v8npg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });
      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletter('');
        setTimeout(() => setNewsletterStatus('idle'), 3000);
      } else {
        setNewsletterStatus('error');
      }
    } catch {
      setNewsletterStatus('error');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navSections = ['home','propiedades-destacadas','properties','mapa-propiedades','contact','about','blog'];
  const navLabels = [t('nav_inicio'), t('nav_destacados'), t('nav_propiedades'), t('nav_mapa'), t('nav_contacto'), t('nav_nosotros'), 'Blog'];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/9fcc4035fe7cce5c6ba56d5b353fae35/5557e1bc3a7b24f13710d01af9330290.jpeg"
              alt="Red Lions Realty Logo"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navSections.map((sec, i) => (
              sec === 'blog'
                ? <Link key={sec} to="/blog" className={`text-base font-medium transition-colors cursor-pointer hover:text-red-600 whitespace-nowrap ${scrolled ? 'text-gray-900' : 'text-white'}`}>Blog</Link>
                : <button key={sec} onClick={() => scrollToSection(sec)} className={`text-base font-medium transition-colors cursor-pointer hover:text-red-600 whitespace-nowrap ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                    {navLabels[i]}
                  </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden lg:block bg-red-600 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav_cta')}
            </button>
            <LanguageSelector currentLang={lang} onChangeLang={setLang} scrolled={scrolled} />

            {/* Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}
            >
              <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-xl border-t border-gray-100">
            <div className="px-4 py-4 space-y-1">
              {navSections.map((sec, i) => (
                sec === 'blog'
                  ? <Link key={sec} to="/blog" onClick={() => setMobileMenuOpen(false)} className="w-full text-left px-4 py-3 text-gray-800 font-medium hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors cursor-pointer block">Blog</Link>
                  : <button key={sec} onClick={() => scrollToSection(sec)} className="w-full text-left px-4 py-3 text-gray-800 font-medium hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors cursor-pointer">
                      {navLabels[i]}
                    </button>
              ))}
              <div className="pt-2">
                <button
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-red-600 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('nav_cta')}
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 1. Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6dafdf5d30775d705ea2bf4b1c479ee2.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 pt-28 pb-40 sm:pb-48">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {t('hero_title')}
            </h1>
            <p className="text-base sm:text-xl text-gray-200 leading-relaxed mb-10">
              {t('hero_sub')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToSection('properties')}
                className="bg-red-600 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                {t('hero_btn1')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-medium hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                {t('hero_btn2')}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Contact Bar */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-3xl bg-white rounded-2xl shadow-2xl px-4 sm:px-8 py-4 sm:py-5 z-20">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-whatsapp-line text-white text-lg sm:text-xl"></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium">{t('contact_label')}</p>
                <p className="text-sm font-bold text-gray-900 truncate">+34744780446</p>
              </div>
            </div>

            <div className="hidden sm:block w-px h-10 bg-gray-200 flex-shrink-0"></div>

            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium">{t('email_label')}</p>
                <p className="text-sm font-bold text-gray-900 truncate">redlionsl.24@gmail.com</p>
              </div>
            </div>

            <div className="hidden sm:block w-px h-10 bg-gray-200 flex-shrink-0"></div>

            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-white text-lg sm:text-xl"></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium">{t('location_label')}</p>
                <p className="text-sm font-bold text-gray-900 truncate">Hurchillo, Alicante</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Videos TikTok - Destacados */}
      <section id="propiedades-destacadas" className="py-20 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block bg-red-50 text-red-600 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              {t('videos_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('videos_title')}
            </h2>
            <p className="text-base sm:text-xl text-gray-500 max-w-2xl mx-auto">
              {t('videos_sub')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12 max-w-4xl mx-auto">
            {/* Video 1 */}
            <div className="flex flex-col items-center">
              <a
                href="https://vt.tiktok.com/ZSuSBwFVt/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="group w-full block cursor-pointer"
                style={{ maxWidth: '340px', margin: '0 auto' }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gray-900" style={{ aspectRatio: '9/16' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=commercial%20local%20store%20front%20alicante%20spain%20street%20real%20estate%20property%20for%20rent%20exterior%20facade%20modern%20building%20entrance%20door&width=340&height=605&seq=tiktok1&orientation=portrait"
                    alt="Local Comercial en Alquiler"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors"></div>
                  <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 rounded-full px-3 py-1.5">
                    <i className="ri-tiktok-line text-white text-base"></i>
                    <span className="text-white text-xs font-semibold">@red.lions.realty</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-gray-900 text-3xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white font-semibold text-sm">{t('video1_type')}</p>
                    <p className="text-gray-300 text-xs mt-0.5">{t('video1_desc')}</p>
                  </div>
                </div>
              </a>
              <a
                href="https://vt.tiktok.com/ZSuSBwFVt/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-5 inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-tiktok-line text-lg"></i>
                {t('videos_btn')}
              </a>
            </div>

            {/* Video 2 */}
            <div className="flex flex-col items-center">
              <a
                href="https://vt.tiktok.com/ZSuSSmVrR/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="group w-full block cursor-pointer"
                style={{ maxWidth: '340px', margin: '0 auto' }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gray-900" style={{ aspectRatio: '9/16' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=commercial%20space%20interior%20staircase%20available%20for%20rent%20real%20estate%20property%20alicante%20spain%20modern%20building%20interior%20entrance%20hallway&width=340&height=605&seq=tiktok2&orientation=portrait"
                    alt="Local Comercial Disponible"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors"></div>
                  <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 rounded-full px-3 py-1.5">
                    <i className="ri-tiktok-line text-white text-base"></i>
                    <span className="text-white text-xs font-semibold">@red.lions.realty</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-gray-900 text-3xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white font-semibold text-sm">{t('video2_type')}</p>
                    <p className="text-gray-300 text-xs mt-0.5">{t('video2_desc')}</p>
                  </div>
                </div>
              </a>
              <a
                href="https://vt.tiktok.com/ZSuSSmVrR/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-5 inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-tiktok-line text-lg"></i>
                {t('videos_btn')}
              </a>
            </div>
          </div>

          <div className="text-center mt-10 sm:mt-14">
            <a
              href="https://www.tiktok.com/@red.lions.realty"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-3 border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-900 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-tiktok-line text-xl"></i>
              {t('videos_more')}
            </a>
          </div>
        </div>
      </section>

      {/* 3. Properties Section */}
      <section id="properties" className="py-20 sm:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="mb-8 sm:mb-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('props_title')}
            </h2>
            <p className="text-base sm:text-xl text-gray-500">{t('props_sub')}</p>
          </div>

          <PropertyFilter onFilter={handleFilter} lang={lang} />

          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <p className="text-gray-500 text-sm">
              <span className="font-semibold text-gray-900">{filteredProperties.length}</span>{' '}
              {filteredProperties.length !== 1 ? t('props_found_pl') : t('props_found')}
              {activeFilters && (
                <span className="ml-2 text-red-500 font-medium">{t('props_active_filters')}</span>
              )}
            </p>
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
              <i className="ri-sort-desc text-gray-400"></i>
              {t('props_sorted')}
            </div>
          </div>

          {filteredProperties.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-product-shop>
              {filteredProperties.map((property) => (
                <div
                  key={property.id}
                  onClick={() => navigate(`/propiedad/${property.slug}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer border border-gray-100 hover:-translate-y-1"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <span className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {property.type}
                    </span>
                    <div className="absolute bottom-4 right-4 bg-white/90 text-gray-900 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-image-line"></i>
                      {t('props_see_photos')}
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{property.price}</p>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{property.title}</h3>
                    <p className="text-gray-600 mb-4 flex items-center gap-2 text-sm sm:text-base">
                      <i className="ri-map-pin-line text-red-600"></i>
                      {property.location}
                    </p>

                    <div className="flex items-center gap-4 sm:gap-6 text-gray-600 mb-4 text-sm sm:text-base">
                      <span className="flex items-center gap-1 sm:gap-2">
                        <i className="ri-hotel-bed-line text-red-600"></i>
                        {property.bedrooms}
                      </span>
                      <span className="flex items-center gap-1 sm:gap-2">
                        <i className="ri-drop-line text-red-600"></i>
                        {property.bathrooms}
                      </span>
                      <span className="flex items-center gap-1 sm:gap-2">
                        <i className="ri-ruler-line text-red-600"></i>
                        {property.area}
                      </span>
                    </div>

                    <p className="text-gray-600 text-sm leading-relaxed">{property.description}</p>

                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-red-600 text-sm font-semibold flex items-center gap-1">
                        {t('props_see_detail')}
                        <i className="ri-arrow-right-line"></i>
                      </span>
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <i className="ri-image-2-line"></i>
                        {t('props_gallery')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-24 bg-white rounded-3xl">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-search-line text-red-500 text-3xl sm:text-4xl"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{t('props_none_title')}</h3>
              <p className="text-gray-500 mb-6 text-sm sm:text-base">{t('props_none_sub')}</p>
            </div>
          )}
        </div>
      </section>

      {/* 4. Properties Map Section */}
      <PropertiesMap properties={allProperties} />

      {/* 4. Contact Section */}
      <section id="contact" className="py-20 sm:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div
            className="relative rounded-3xl overflow-hidden"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=modern%20architecture%20interior%20with%20natural%20light%2C%20minimalist%20design%2C%20clean%20lines%2C%20contemporary%20space%2C%20warm%20ambient%20lighting%20creating%20strong%20contrast%2C%20professional%20architectural%20photography%2C%20simple%20elegant%20background&width=1400&height=900&seq=contact1&orientation=landscape)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/65 to-black/70"></div>

            <div className="relative z-10 px-5 sm:px-10 lg:px-16 py-12 sm:py-20">
              <div className="text-center mb-10 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 sm:mb-6">
                  {t('contact_title')}
                </h2>
                <p className="text-base sm:text-xl text-gray-300">
                  {t('contact_sub')}
                </p>
              </div>

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-5 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <input
                    type="text"
                    name="name"
                    placeholder={t('contact_name')}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-white/20 border border-white/30 rounded-xl px-5 py-4 text-white placeholder-gray-300 focus:outline-none focus:border-white/50 transition-colors text-sm"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder={t('contact_email')}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-white/20 border border-white/30 rounded-xl px-5 py-4 text-white placeholder-gray-300 focus:outline-none focus:border-white/50 transition-colors text-sm"
                    required
                  />
                </div>

                <input
                  type="tel"
                  name="phone"
                  placeholder={t('contact_phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full bg-white/20 border border-white/30 rounded-xl px-5 py-4 text-white placeholder-gray-300 focus:outline-none focus:border-white/50 transition-colors text-sm"
                  required
                />

                <textarea
                  name="message"
                  placeholder={t('contact_message')}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  maxLength={500}
                  rows={4}
                  className="w-full bg-white/20 border border-white/30 rounded-xl px-5 py-4 text-white placeholder-gray-300 focus:outline-none focus:border-white/50 transition-colors resize-none text-sm"
                ></textarea>

                {formStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500/50 text-white px-6 py-3 rounded-xl text-center text-sm">
                    {t('contact_success')}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500/50 text-white px-6 py-3 rounded-xl text-center text-sm">
                    {t('contact_error')}
                  </div>
                )}

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full sm:w-auto bg-white text-gray-900 px-10 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'sending' ? t('contact_sending') : t('contact_send')}
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* 5. About Section */}
      <section id="about" className="py-20 sm:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block bg-white text-red-600 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              {t('about_badge')}
            </span>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6 sm:mb-8">
                {t('about_title')}
              </h2>
              <p className="text-base sm:text-xl text-gray-600 leading-relaxed mb-10 sm:mb-12">
                {t('about_sub')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-16 sm:mb-20">
            {[
              { icon: 'ri-home-heart-line', titleKey: 'about_mission_title', textKey: 'about_mission_text' },
              { icon: 'ri-eye-line', titleKey: 'about_vision_title', textKey: 'about_vision_text' },
              { icon: 'ri-star-line', titleKey: 'about_values_title', textKey: 'about_values_text' },
            ].map((item) => (
              <div key={item.titleKey} className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-5">
                  <i className={`${item.icon} text-red-600 text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{t(item.titleKey)}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{t(item.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Why Choose Us Section */}
      <section className="py-20 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">{t('why_title')}</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('why_sub')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10">
            {[
              { icon: 'ri-search-line', titleKey: 'why1_title', textKey: 'why1_text' },
              { icon: 'ri-shield-check-line', titleKey: 'why2_title', textKey: 'why2_text' },
              { icon: 'ri-customer-service-2-line', titleKey: 'why3_title', textKey: 'why3_text' },
            ].map((item) => (
              <div key={item.titleKey} className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
                  <i className={`${item.icon} text-white text-3xl sm:text-4xl`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{t(item.titleKey)}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{t(item.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Location / Map Section */}
      <section id="ubicacion" className="py-20 sm:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block bg-red-50 text-red-600 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              {t('location_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('location_title')}
            </h2>
            <p className="text-base sm:text-xl text-gray-500 max-w-2xl mx-auto">
              {t('location_sub')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-stretch">
            {/* Map */}
            <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ minHeight: '320px' }}>
              <iframe
                title="Ubicación Red Lions Realty - Hurchillo, Alicante"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d500!2d-0.9222!3d38.0594!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd6278b8b8b8b8b8b%3A0x0!2sPlaza+de+la+Iglesia%2C+Hurchillo%2C+03313+Orihuela%2C+Alicante!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '320px', display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Info Panel */}
            <div className="flex flex-col justify-center gap-6 sm:gap-8">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Red Lions Realty</h3>
                <p className="text-gray-500 text-base sm:text-lg leading-relaxed">
                  {t('location_desc')}
                </p>
              </div>

              <div className="space-y-5 sm:space-y-6">
                {[
                  {
                    icon: 'ri-map-pin-2-line',
                    labelKey: 'location_address_label',
                    lines: ['Hurchillo, Alicante', 'Esquina frente a la iglesia, al lado del Banco Caja Rural', 'Vega Baja del Segura, 03313']
                  },
                  {
                    icon: 'ri-phone-line',
                    labelKey: 'location_phone_label',
                    lines: ['+34 744 780 446']
                  },
                  {
                    icon: 'ri-mail-line',
                    labelKey: 'location_email_label',
                    lines: ['redlionsl.24@gmail.com']
                  },
                  {
                    icon: 'ri-time-line',
                    labelKey: 'location_hours_label',
                    lines: [t('location_hours1'), t('location_hours2')]
                  },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 sm:gap-5">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-white text-xl sm:text-2xl`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase mb-1">{t(item.labelKey)}</p>
                      {item.lines.map((line, li) => (
                        <p key={li} className={`${li === 0 ? 'text-gray-900 font-semibold text-base sm:text-lg' : 'text-gray-500 text-sm sm:text-base'}`}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="https://www.google.com/maps/search/Hurchillo,+Alicante,+03313,+Spain"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 bg-red-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer w-fit"
              >
                <i className="ri-navigation-line text-xl"></i>
                {t('location_directions')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('blog_title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Guías, consejos y novedades sobre el mercado inmobiliario en la Costa Blanca
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {blogPosts.slice(0, 3).map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center gap-2 text-red-600 font-medium group-hover:gap-3 transition-all">
                    <span>{t('blog_read_more')}</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-3 rounded-xl hover:bg-red-700 transition-colors font-medium whitespace-nowrap"
            >
              <span>{t('blog_see_all')}</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. Testimonials Section */}
      <section id="testimonials" className="py-20 sm:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="mb-12 sm:mb-16">
            <span className="inline-block bg-gray-100 text-gray-900 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              {t('testimonials_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight whitespace-pre-line">
              {t('testimonials_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center gap-4 mb-5 sm:mb-6">
                  <div
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-gray-900 font-bold text-base sm:text-lg flex-shrink-0"
                    style={{ backgroundColor: testimonial.color }}
                  >
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                  "{testimonial.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-10 sm:mt-20 mx-3 sm:mx-8 mb-4 sm:mb-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-6 sm:px-16 py-12 sm:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 sm:gap-12 mb-12 sm:mb-16">
            <div className="lg:col-span-5">
              <h3 className="text-2xl sm:text-4xl font-light mb-4">{t('footer_newsletter_title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
                {t('footer_newsletter_sub')}
              </p>

              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-5 sm:space-y-6">
                <input
                  type="email"
                  name="email"
                  placeholder={t('footer_newsletter_placeholder')}
                  value={newsletter}
                  onChange={(e) => setNewsletter(e.target.value)}
                  className="w-full bg-transparent border-b border-gray-600 pb-3 text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors text-sm sm:text-base"
                  required
                />
                {newsletterStatus === 'success' && (
                  <div className="text-green-400 text-sm">{t('footer_newsletter_success')}</div>
                )}
                {newsletterStatus === 'error' && (
                  <div className="text-red-400 text-sm">{t('footer_newsletter_error')}</div>
                )}
                <button
                  type="submit"
                  disabled={newsletterStatus === 'sending'}
                  className="bg-white text-gray-900 px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 text-sm sm:text-base"
                >
                  {newsletterStatus === 'sending' ? t('footer_newsletter_sending') : t('footer_newsletter_btn')}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs sm:text-sm font-semibold uppercase mb-5 sm:mb-6 text-gray-400">{t('footer_nav_label')}</h4>
              <div className="space-y-3 sm:space-y-4">
                {['home','propiedades-destacadas','properties','mapa-propiedades','contact','about'].map((sec, i) => {
                  const labelKeys = ['nav_inicio_label','nav_destacados_label','nav_propiedades_label','nav_mapa_label','nav_contacto_label','nav_nosotros_label'];
                  return (
                    <button key={sec} onClick={() => scrollToSection(sec)} className="block text-white hover:text-red-400 transition-colors cursor-pointer text-left text-sm sm:text-base">
                      {t(labelKeys[i])}
                    </button>
                  );
                })}
                <Link to="/blog" className="block text-white hover:text-red-400 transition-colors cursor-pointer text-sm sm:text-base">Blog</Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <h4 className="text-xs sm:text-sm font-semibold uppercase mb-5 sm:mb-6 text-gray-400">{t('footer_contact_label')}</h4>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base">
                <p className="text-white">+34744780446</p>
                <p className="text-white break-all">redlionsl.24@gmail.com</p>
                <div className="pt-3 sm:pt-4">
                  <h5 className="text-xs sm:text-sm font-semibold uppercase mb-2 sm:mb-3 text-gray-400">{t('location_badge')}</h5>
                  <p className="text-white leading-relaxed">
                    Hurchillo, Alicante<br />
                    Vega Baja 03313
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center sm:justify-start">
              <a href="#" rel="nofollow" className="text-white hover:text-red-400 transition-colors text-sm sm:text-base">Facebook</a>
              <a href="https://www.instagram.com/redlionsrealty" target="_blank" rel="nofollow noopener noreferrer" className="text-white hover:text-red-400 transition-colors text-sm sm:text-base">Instagram</a>
              <a href="https://www.tiktok.com/@red.lions.realty" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-1.5 text-white hover:text-red-400 transition-colors text-sm sm:text-base whitespace-nowrap">
                <i className="ri-tiktok-line text-base"></i>
                TikTok
              </a>
              <a href="#" rel="nofollow" className="text-white hover:text-red-400 transition-colors text-sm sm:text-base">LinkedIn</a>
            </div>

            <p className="text-gray-400 text-xs sm:text-sm text-center">
              {t('footer_rights')}
            </p>

            <div className="flex items-center gap-4 sm:gap-6">
              <a href="#" rel="nofollow" className="text-white hover:text-red-400 transition-colors text-sm sm:text-base">{t('footer_privacy')}</a>
              <a href="#" rel="nofollow" className="text-white hover:text-red-400 transition-colors text-sm sm:text-base">{t('footer_terms')}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3">
        {showScrollTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-13 h-13 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300 cursor-pointer flex items-center justify-center"
            style={{ width: '52px', height: '52px' }}
            aria-label="Volver arriba"
          >
            <i className="ri-arrow-up-line text-xl"></i>
          </button>
        )}

        <a
          href="https://wa.me/34744780446"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex items-center justify-center rounded-full shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer"
          style={{ width: '56px', height: '56px', backgroundColor: '#25D366' }}
          aria-label="Contactar por WhatsApp"
        >
          <i className="ri-whatsapp-line text-white text-2xl"></i>
        </a>
      </div>
    </div>
  );
}
